import { useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';

import { GetProductReviewsDocument } from 'src/gql/graphql';
import type { GetProductReviewsQuery } from 'src/gql/graphql';

export type ProductReview = NonNullable<GetProductReviewsQuery['getProductReviews']>['productReviews'][number];

export const REVIEWS_PAGE_SIZE = 5;

type UseProductReviewsArgs = {
  productId?: string | null;
  limit?: number;
  skip?: boolean;
};

type UseProductReviewsResult = {
  reviews: ProductReview[];
  totalCount: number;
  hasMore: boolean;
  loading: boolean;
  loadMore: () => void;
};

/**
 * Paginated list of approved consumer reviews for a product. Starts at page 1
 * and appends subsequent pages via `loadMore` (Apollo `fetchMore`).
 */
export function useProductReviews({
  productId,
  limit = REVIEWS_PAGE_SIZE,
  skip = false,
}: UseProductReviewsArgs): UseProductReviewsResult {
  const [loadedPages, setLoadedPages] = useState(1);
  // Set once a fetched page comes back short, so the fallback `hasMore` can't
  // loop forever when `meta.totalCount` is absent (e.g. exactly one full page).
  const [exhausted, setExhausted] = useState(false);

  const { data, loading, fetchMore } = useQuery(GetProductReviewsDocument, {
    variables: { productId: productId ?? '', page: 1, limit },
    skip: skip || !productId,
    notifyOnNetworkStatusChange: true,
  });

  const reviews = data?.getProductReviews?.productReviews ?? [];
  const metaTotal = data?.getProductReviews?.meta?.totalCount;
  const totalCount = metaTotal ?? reviews.length;
  // Prefer the server total; if `meta` is absent, fall back to "did the last
  // page come back full?" — but stop once a page returns short (`exhausted`).
  const hasMore =
    !exhausted && (metaTotal != null ? reviews.length < metaTotal : reviews.length > 0 && reviews.length % limit === 0);

  const loadMore = useCallback((): void => {
    if (loading || !hasMore) return;
    const nextPage = loadedPages + 1;
    void fetchMore({
      variables: { productId: productId ?? '', page: nextPage, limit },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult.getProductReviews) return prev;
        const prevReviews = prev.getProductReviews?.productReviews ?? [];
        const seen = new Set(prevReviews.map((review) => review.id));
        const merged = [
          ...prevReviews,
          ...fetchMoreResult.getProductReviews.productReviews.filter((review) => !seen.has(review.id)),
        ];
        return { getProductReviews: { ...fetchMoreResult.getProductReviews, productReviews: merged } };
      },
    })
      .then((result) => {
        setLoadedPages(nextPage);
        // A page shorter than `limit` (incl. empty) means there's nothing left.
        if ((result.data.getProductReviews?.productReviews.length ?? 0) < limit) {
          setExhausted(true);
        }
      })
      .catch(() => {
        // Leave `loadedPages`/`hasMore` unchanged so the button can be retried.
      });
  }, [loading, hasMore, loadedPages, fetchMore, productId, limit]);

  return { reviews, totalCount, hasMore, loading, loadMore };
}
